/**
 * Helper para llamar a endpoints Pando asíncronos (HTTP 202 + polling).
 *
 * Backend: endpoints largos de Pando (Claude/Anthropic) ahora devuelven 202 con
 * `{ job_id, status: "running" }`. Luego el cliente debe hacer polling de
 * `GET /api/ai-jobs/{job_id}` hasta `status === "done"`, momento en el que
 * `result` contiene la respuesta que solía devolver el endpoint síncrono.
 *
 * Motivo: Cloudflare cierra conexiones HTTP a los 100 segundos (error 524).
 *
 * Uso típico (drop-in replacement de axios.post):
 *
 *     import { postAiJob } from "../utils/aiJobs";
 *     try {
 *       const result = await postAiJob(`${API}/licitaciones/.../compare`, body);
 *       // result es lo que devolvía el endpoint síncrono original
 *     } catch (e) { ... }
 */
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

/**
 * POST a un endpoint que ahora devuelve 202+job_id, y espera el resultado.
 * Si la respuesta ya es síncrona (no devuelve `job_id`), se retorna r.data tal cual.
 */
export async function postAiJob(url, body, opts = {}) {
  const r = await axios.post(url, body);
  if (r.data && r.data.job_id && r.data.status === "running") {
    return await pollAiJob(r.data.job_id, opts);
  }
  return r.data;
}

/**
 * Polling pasivo de un job conocido por su ID.
 *  - pollMs: intervalo entre peticiones (default 4 s)
 *  - maxMs:  timeout total (default 15 min)
 *  - onTick: callback opcional(snapshot) — útil para mostrar ETA / progreso
 */
export async function pollAiJob(
  jobId,
  { pollMs = 4000, maxMs = 15 * 60 * 1000, onTick } = {}
) {
  if (!jobId) throw new Error("pollAiJob: jobId vacío");
  const t0 = Date.now();
  while (Date.now() - t0 < maxMs) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(res => setTimeout(res, pollMs));
    // eslint-disable-next-line no-await-in-loop
    const s = await axios.get(`${API}/ai-jobs/${jobId}`);
    if (onTick) try { onTick(s.data); } catch { /* swallow UI errors */ }
    const st = s.data?.status;
    if (st === "done") return s.data.result;
    if (st === "failed" || st === "cancelled" || st === "not_found") {
      throw new Error(s.data?.error || `Job ${st}`);
    }
    // running → keep polling
  }
  throw new Error("Timeout esperando el job (15 min).");
}
